import type { ChangeEvent } from 'react'
import type {
  ClaimContact,
  CompleteOnboarding,
  SendContactClaimOtp,
  UpdateMyProfile,
} from '@/modules/auth/use-cases'
import type {
  LoadCurrentDocument,
  RecordLegalAcceptance,
} from '@/modules/legal/use-cases'
import {
  Button,
  Input,
  Label,
  LegalCheckboxes,
  ProfilePhotoUpload,
  SeparatedPhoneInput,
} from '@/shared/ui/primitives'
import { FlywaySelect } from '@/shared/ui/components'
import { formatResendLabel } from '@/shared/ui/hooks'
import type { HunterProfileData } from '../types'
import { useHunterProfileStep } from './use-hunter-profile-step'

export type HunterProfileStepProps = {
  initialValue: HunterProfileData
  lockedEmail?: string | null
  locked: boolean
  nextOnboardingStep: number
  onUpdateMyProfile: UpdateMyProfile
  onCompleteOnboarding: CompleteOnboarding
  onSendContactClaimOtp: SendContactClaimOtp
  onClaimContact: ClaimContact
  onRecordLegalAcceptance: RecordLegalAcceptance
  onLoadCurrentDocument: LoadCurrentDocument
  onUpload: (file: File) => Promise<string>
  onPersistData: (data: HunterProfileData) => void
  onSuccess: () => void
}

export function HunterProfileStep(props: HunterProfileStepProps) {
  const vm = useHunterProfileStep(props)

  return (
    <form
      onSubmit={vm.handleSubmit}
      className="flex flex-col gap-4"
      data-testid="hunter-profile-form"
      noValidate
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="hunter-profile-photo">Profile photo</Label>
        <ProfilePhotoUpload
          value={vm.profilePhotoUrl}
          onChange={vm.setProfilePhotoUrl}
          onUpload={vm.handleUpload}
          disabled={vm.isSubmitting || vm.isLocked}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="hunter-profile-first-name">First name</Label>
          <Input
            id="hunter-profile-first-name"
            value={vm.firstName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setFirstName(e.target.value)}
            autoComplete="given-name"
            disabled={vm.isLocked}
          />
          {vm.fieldErrors.firstName ? (
            <p role="alert" className="text-xs text-destructive">
              {vm.fieldErrors.firstName}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="hunter-profile-last-name">Last name</Label>
          <Input
            id="hunter-profile-last-name"
            value={vm.lastName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setLastName(e.target.value)}
            autoComplete="family-name"
            disabled={vm.isLocked}
          />
          {vm.fieldErrors.lastName ? (
            <p role="alert" className="text-xs text-destructive">
              {vm.fieldErrors.lastName}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="hunter-profile-contact">Email</Label>
        <Input
          id="hunter-profile-contact"
          type="email"
          value={vm.contactValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setContactValue(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          disabled={vm.isEmailLocked || vm.isLocked}
        />
        {vm.fieldErrors.contactValue ? (
          <p role="alert" className="text-xs text-destructive">
            {vm.fieldErrors.contactValue}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="hunter-profile-phone">Phone number</Label>
        <SeparatedPhoneInput
          id="hunter-profile-phone"
          value={vm.phoneE164}
          onChange={vm.setPhone}
          placeholder="555 123 4567"
          autoComplete="tel"
          disabled={vm.isSubmitting || vm.isLocked}
        />
        {vm.fieldErrors.phone ? (
          <p role="alert" className="text-xs text-destructive">
            {vm.fieldErrors.phone}
          </p>
        ) : null}
      </div>
      {vm.contactClaim ? (
        <div
          className="flex flex-col gap-2 rounded-lg border bg-card p-3"
          data-testid="contact-claim-panel"
        >
          <p className="text-sm">
            {vm.contactClaim.pendingInvite
              ? vm.contactClaim.identifierType === 'email'
                ? 'You have a pending invite for this email. Verify it to claim your profile.'
                : 'This phone number is on a profile with a pending invite. Verify it to claim it as yours.'
              : vm.contactClaim.holderUnregistered
                ? vm.contactClaim.identifierType === 'email'
                  ? 'This email belongs to an unfinished signup. Verify it to claim it as yours.'
                  : 'This phone number belongs to an unfinished signup. Verify it to claim it as yours.'
                : vm.contactClaim.identifierType === 'email'
                ? 'This email is on another account but unverified. Verify it to claim it as yours.'
                : 'This phone number is on another account but unverified. Verify it to claim it as yours.'}
          </p>
          {vm.contactClaim.codeSent ? (
            <>
              <div className="flex items-center gap-2">
                <Input
                  id="contact-claim-code"
                  inputMode="numeric"
                  value={vm.claimCode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setClaimCode(e.target.value)}
                  placeholder="Enter code"
                  aria-label="Verification code"
                />
                <Button
                  type="button"
                  onClick={vm.handleConfirmClaim}
                  disabled={vm.isClaiming || vm.claimCode.trim() === ''}
                >
                  {vm.isClaiming ? 'Verifying…' : 'Verify & claim'}
                </Button>
              </div>
              <div className="flex items-center justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={vm.handleSendClaimCode}
                  disabled={vm.isSendingClaimCode || vm.isClaiming || vm.claimResendSeconds > 0}
                >
                  {vm.claimResendSeconds > 0 ? formatResendLabel(vm.claimResendSeconds) : 'Resend code'}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="outline" onClick={vm.handleCancelClaim}>
                Use a different {vm.contactClaim.identifierType}
              </Button>
              <Button
                type="button"
                onClick={vm.handleSendClaimCode}
                disabled={vm.isSendingClaimCode || vm.isClaiming || vm.claimResendSeconds > 0}
              >
                {vm.claimResendSeconds > 0 ? formatResendLabel(vm.claimResendSeconds) : 'Send me a code'}
              </Button>
            </div>
          )}
          {vm.claimError ? (
            <p role="alert" className="text-xs text-destructive">
              {vm.claimError}
            </p>
          ) : null}
        </div>
      ) : null}
      <FlywaySelect
        id="hunter-profile-flyway"
        required
        value={vm.flywayRegion}
        onValueChange={vm.setFlywayRegion}
        error={vm.fieldErrors.flywayRegion}
        disabled={vm.isSubmitting || vm.isLocked}
      />
      {vm.isLocked ? null : (
        <div className="flex flex-col gap-2">
          <LegalCheckboxes
            tosAccepted={vm.tosAccepted}
            privacyAccepted={vm.privacyAccepted}
            onTosChange={vm.setTosAccepted}
            onPrivacyChange={vm.setPrivacyAccepted}
          />
          {vm.fieldErrors.tosAccepted ? (
            <p role="alert" className="text-xs text-destructive">
              {vm.fieldErrors.tosAccepted}
            </p>
          ) : null}
          {vm.fieldErrors.privacyAccepted ? (
            <p role="alert" className="text-xs text-destructive">
              {vm.fieldErrors.privacyAccepted}
            </p>
          ) : null}
        </div>
      )}
      {vm.submitError ? (
        <p role="alert" className="text-sm text-destructive">
          {vm.submitError}
        </p>
      ) : null}
      <Button
        type="submit"
        disabled={vm.isSubmitting || (!vm.isLocked && !vm.documentsReady)}
        className="w-full"
        data-testid="hunter-profile-submit"
      >
        {vm.isSubmitting ? 'Saving…' : 'Continue'}
      </Button>
    </form>
  )
}
